import React, { useState, useRef } from 'react';

const FileSplitLLM = () => {
  const [selectedApproach, setSelectedApproach] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const contentRef = useRef(null);

  // Function to download as PNG
  const downloadAsPNG = async (elementRef = contentRef, filename = null) => {
    setIsExporting(true);
    
    try {
      // Dynamically import html2canvas
      const html2canvas = (await import('html2canvas')).default;
      
      if (elementRef.current) {
        const canvas = await html2canvas(elementRef.current, {
          backgroundColor: '#0F2027',
          scale: 2, // Higher quality
          logging: false,
          windowWidth: elementRef.current.scrollWidth,
          windowHeight: elementRef.current.scrollHeight
        });
        
        // Convert to blob and download
        canvas.toBlob((blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename || `llm-file-optimization-${selectedApproach === 1 ? 'direct' : selectedApproach === 2 ? 'class-split' : selectedApproach === 3 ? 'smart-split' : selectedApproach === 4 ? 'simple-view' : 'agent-based'}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        });
      }
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      alert('Error exporting to PNG. Please make sure html2canvas is installed.');
    } finally {
      setIsExporting(false);
    }
  };

  // Create refs for individual sections
  const approach1Ref = useRef(null);
  const approach2Ref = useRef(null);
  const approach3Ref = useRef(null);
  const approach4Ref = useRef(null);
  const approach5Ref = useRef(null);
  const comparisonTableRef = useRef(null);

  // Token visualization component
  const TokenBlock = ({ tokens, maxTokens, highlighted = [], label, color = '#666' }) => {
    const tokenRatio = Math.min(tokens / maxTokens, 1);
    const overflowTokens = Math.max(0, tokens - maxTokens);
    
    return (
      <div style={{
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '8px',
        padding: '15px',
        position: 'relative',
        border: `2px solid ${color}`,
        minHeight: '120px'
      }}>
        <div style={{
          position: 'absolute',
          top: '-12px',
          left: '10px',
          background: '#0F2027',
          padding: '2px 10px',
          fontSize: '12px',
          color: color,
          fontWeight: '600'
        }}>
          {label}
        </div>
        
        {/* Token bar */}
        <div style={{
          height: '40px',
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
          overflow: 'hidden',
          marginBottom: '10px',
          position: 'relative'
        }}>
          <div style={{
            height: '100%',
            width: `${tokenRatio * 100}%`,
            background: tokens > maxTokens ? '#FF6B6B' : color,
            transition: 'width 0.5s ease',
            display: 'flex',
            alignItems: 'center',
            paddingLeft: '10px'
          }}>
            <span style={{ fontSize: '12px', fontWeight: '600' }}>
              {tokens.toLocaleString()} tokens
            </span>
          </div>
          {tokens > maxTokens && (
            <div style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              fontSize: '12px',
              color: '#FF6B6B',
              fontWeight: '600'
            }}>
              +{overflowTokens.toLocaleString()} overflow
            </div>
          )}
        </div>
        
        {/* Additional info */}
        <div style={{ fontSize: '12px', opacity: 0.8 }}>
          {tokens > maxTokens ? '⚠️ Exceeds context window' : '✅ Within context'}
        </div>
      </div>
    );
  };

  // Code preview component
  const CodePreview = ({ title, lines, tokens, color }) => (
    <div style={{
      background: 'rgba(0, 0, 0, 0.4)',
      borderRadius: '8px',
      padding: '15px',
      border: `1px solid ${color}40`,
      marginBottom: '15px'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '10px',
        fontSize: '14px',
        fontWeight: '600',
        color: color
      }}>
        <span>{title}</span>
        <span>{lines} lines • {tokens.toLocaleString()} tokens</span>
      </div>
      <div style={{
        fontSize: '12px',
        fontFamily: 'monospace',
        opacity: 0.7,
        lineHeight: '1.4'
      }}>
        <div>class DataProcessor:</div>
        <div>    def __init__(self):</div>
        <div>        self.cache = {}</div>
        <div>        ...</div>
        <div style={{ opacity: 0.5 }}>    # ... 1,950+ more lines</div>
      </div>
    </div>
  );

  // Split visualization
  const SplitVisualization = ({ splits, color }) => (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '10px',
      marginBottom: '20px'
    }}>
      {splits.map((split, i) => (
        <div key={i} style={{
          background: 'rgba(0, 0, 0, 0.3)',
          border: `1px solid ${color}40`,
          borderRadius: '8px',
          padding: '12px',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: color,
            marginBottom: '4px'
          }}>{split.name}</div>
          <div style={{
            fontSize: '12px',
            opacity: 0.8
          }}>{split.lines} lines</div>
          <div style={{
            fontSize: '11px',
            opacity: 0.6
          }}>{split.tokens} tokens</div>
        </div>
      ))}
    </div>
  );

  // Model recommendation component
  const ModelRecommendation = ({ models, color }) => (
    <div style={{
      background: `${color}20`,
      border: `1px solid ${color}40`,
      borderRadius: '8px',
      padding: '15px',
      marginBottom: '20px'
    }}>
      <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: color }}>
        📊 Recommended Models
      </h4>
      <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
        {models.map((model, i) => (
          <div key={i} style={{ marginBottom: '8px' }}>
            <strong>{model.name}:</strong> {model.reason}
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
      padding: '40px 20px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <h1 style={{ 
              margin: '0 0 15px 0', 
              fontSize: '36px',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              🤖 LLM File Optimization: Token Limits & Strategies
            </h1>
            <p style={{ margin: '0', fontSize: '18px', opacity: 0.8 }}>
              Understanding how different approaches handle large files with token constraints
            </p>
          </div>
          
          {/* Download Button */}
          <button
            onClick={() => downloadAsPNG(contentRef, `llm-file-optimization-${selectedApproach === 1 ? 'direct' : selectedApproach === 2 ? 'class-split' : selectedApproach === 3 ? 'smart-split' : selectedApproach === 4 ? 'simple-view' : 'agent-based'}.png`)}
            disabled={isExporting}
            style={{
              padding: '12px 24px',
              background: isExporting ? '#666' : 'linear-gradient(135deg, #667eea, #764ba2)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              marginTop: '10px'
            }}
          >
            {isExporting ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Exporting...
              </>
            ) : (
              <>
                📸 Download as PNG
              </>
            )}
          </button>
        </div>

        <style>
          {`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}
        </style>

        {/* Content to be exported */}
        <div ref={contentRef}>
          <div style={{
            background: 'rgba(102, 126, 234, 0.1)',
            padding: '20px',
            borderRadius: '12px',
            border: '1px solid rgba(102, 126, 234, 0.3)',
            marginBottom: '30px'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#667eea' }}>
              📄 Example File: data_processor.py
            </h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              fontSize: '14px'
            }}>
              <div>
                <strong>Total Lines:</strong> 2,000 lines
              </div>
              <div>
                <strong>Total Tokens:</strong> ~15,000 tokens
              </div>
              <div>
                <strong>Average:</strong> ~7.5 tokens/line
              </div>
              <div>
                <strong>Context Window:</strong> 8,000 tokens (typical)
              </div>
            </div>
          </div>

          {/* Approach Selector */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '40px',
            flexWrap: 'wrap'
          }}>
            {[
              { id: 1, label: 'Direct LLM', color: '#FF6B6B' },
              { id: 5, label: 'Agent-Based', color: '#FFA726' },
              { id: 2, label: 'Class/Function Split', color: '#4ECDC4' },
              { id: 3, label: 'Smart Token Split', color: '#96CEB4' },
              { id: 4, label: 'Simple View', color: '#9C27B0' }
            ].map(approach => (
              <button
                key={approach.id}
                onClick={() => setSelectedApproach(approach.id)}
                style={{
                  padding: '12px 24px',
                  background: selectedApproach === approach.id 
                    ? approach.color 
                    : `${approach.color}20`,
                  color: selectedApproach === approach.id ? 'white' : approach.color,
                  border: `2px solid ${approach.color}`,
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                {approach.label}
              </button>
            ))}
          </div>

          {/* Approach 1: Direct LLM */}
          {selectedApproach === 1 && (
            <div ref={approach1Ref}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '30px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                {/* Download button for this approach */}
                <button
                  onClick={() => downloadAsPNG(approach1Ref, 'llm-optimization-direct.png')}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    padding: '8px 16px',
                    background: 'rgba(255, 107, 107, 0.2)',
                    color: '#FF6B6B',
                    border: '1px solid #FF6B6B',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📸 Export
                </button>

                <h2 style={{ margin: '0 0 25px 0', color: '#FF6B6B' }}>
                  Approach 1: Direct LLM Processing
                </h2>

                {/* Visual Process */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  gap: '20px',
                  alignItems: 'center',
                  marginBottom: '30px'
                }}>
                  <div>
                    <CodePreview 
                      title="Original File"
                      lines={2000}
                      tokens={15000}
                      color="#FF6B6B"
                    />
                    <TokenBlock
                      tokens={15000}
                      maxTokens={8000}
                      label="File Token Count"
                      color="#FF6B6B"
                    />
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '36px' }}>→</div>
                    <div style={{
                      background: 'rgba(255, 107, 107, 0.2)',
                      padding: '10px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      marginTop: '10px'
                    }}>
                      🤖 LLM
                    </div>
                  </div>
                  
                  <div>
                    <div style={{
                      background: 'rgba(255, 107, 107, 0.1)',
                      padding: '15px',
                      borderRadius: '8px',
                      marginBottom: '15px'
                    }}>
                      <h4 style={{ margin: '0 0 10px 0', color: '#FF6B6B' }}>
                        ⚠️ Context Overflow
                      </h4>
                      <div style={{ fontSize: '13px' }}>
                        • Only processes first ~1,000 lines
                        <br />• Misses 50% of the code
                        <br />• No awareness of dependencies
                      </div>
                    </div>
                    
                    <div style={{
                      background: 'rgba(0, 0, 0, 0.3)',
                      padding: '15px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontFamily: 'monospace'
                    }}>
                      <div style={{ color: '#FF6B6B', marginBottom: '5px' }}>
                        // LLM Response (Incomplete):
                      </div>
                      <div>// Optimized first 1000 lines only</div>
                      <div>// ❌ Missing critical functions</div>
                      <div>// ❌ Broken dependencies</div>
                    </div>
                  </div>
                </div>

                <ModelRecommendation
                  models={[
                    { name: 'Standard Context Models', reason: 'Will truncate large files' },
                    { name: 'Large Context Models', reason: 'Can handle bigger files' },
                    { name: 'Extended Context Models', reason: 'Best for very large files' }
                  ]}
                  color="#FF6B6B"
                />

                {/* Pros and Cons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px'
                }}>
                  <div style={{
                    background: 'rgba(76, 175, 80, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#4CAF50' }}>✅ Pros</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Simple implementation</li>
                      <li>Fast processing time</li>
                      <li>No complex logic needed</li>
                      <li>Single API call</li>
                    </ul>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255, 107, 107, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 107, 107, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#FF6B6B' }}>❌ Cons</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Loses significant portions of code</li>
                      <li>Quality degrades with file size</li>
                      <li>Breaks dependencies</li>
                      <li>Low success rate</li>
                      <li>No awareness of truncated content</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approach 2: Class/Function Split */}
          {selectedApproach === 2 && (
            <div ref={approach2Ref}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '30px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                {/* Download button for this approach */}
                <button
                  onClick={() => downloadAsPNG(approach2Ref, 'llm-optimization-class-split.png')}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    padding: '8px 16px',
                    background: 'rgba(78, 205, 196, 0.2)',
                    color: '#4ECDC4',
                    border: '1px solid #4ECDC4',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📸 Export
                </button>

                <h2 style={{ margin: '0 0 25px 0', color: '#4ECDC4' }}>
                  Approach 2: Class/Function Splitting
                </h2>

                {/* Split Visualization */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>
                    File Split by Structure
                  </h3>
                  
                  <SplitVisualization
                    splits={[
                      { name: 'DataLoader', lines: 400, tokens: 3000 },
                      { name: 'Processor', lines: 600, tokens: 4500 },
                      { name: 'Validator', lines: 300, tokens: 2250 },
                      { name: 'Utils', lines: 200, tokens: 1500 },
                      { name: 'Transformer', lines: 500, tokens: 3750 }
                    ]}
                    color="#4ECDC4"
                  />

                  {/* Process Flow */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#4ECDC4' }}>
                      Processing Flow
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '15px',
                      fontSize: '13px'
                    }}>
                      <div style={{
                        background: 'rgba(78, 205, 196, 0.1)',
                        padding: '15px',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', marginBottom: '5px' }}>1️⃣</div>
                        <strong>Split by AST</strong>
                        <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '5px' }}>
                          Parse classes & functions
                        </div>
                      </div>
                      
                      <div style={{
                        background: 'rgba(78, 205, 196, 0.1)',
                        padding: '15px',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', marginBottom: '5px' }}>2️⃣</div>
                        <strong>Process Each</strong>
                        <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '5px' }}>
                          5 separate LLM calls
                        </div>
                      </div>
                      
                      <div style={{
                        background: 'rgba(78, 205, 196, 0.1)',
                        padding: '15px',
                        borderRadius: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '24px', marginBottom: '5px' }}>3️⃣</div>
                        <strong>Merge Results</strong>
                        <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '5px' }}>
                          Combine optimizations
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Token Distribution */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '10px',
                    marginBottom: '20px'
                  }}>
                    {[
                      { name: 'DataLoader', tokens: 3000 },
                      { name: 'Processor', tokens: 4500 },
                      { name: 'Validator', tokens: 2250 },
                      { name: 'Utils', tokens: 1500 },
                      { name: 'Transformer', tokens: 3750 }
                    ].map((item, i) => (
                      <TokenBlock
                        key={i}
                        tokens={item.tokens}
                        maxTokens={8000}
                        label={item.name}
                        color="#4ECDC4"
                      />
                    ))}
                  </div>
                </div>

                <ModelRecommendation
                  models={[
                    { name: 'Small Context Models', reason: 'Efficient for smaller chunks' },
                    { name: 'Medium Context Models', reason: 'Better optimization quality' },
                    { name: 'Code-Specialized Models', reason: 'Optimized for code understanding' }
                  ]}
                  color="#4ECDC4"
                />

                {/* Pros and Cons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px'
                }}>
                  <div style={{
                    background: 'rgba(76, 175, 80, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#4CAF50' }}>✅ Pros</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>All code gets processed</li>
                      <li>Respects logical boundaries</li>
                      <li>Moderate success rate</li>
                      <li>Works with standard models</li>
                      <li>Preserves class/function structure</li>
                    </ul>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255, 107, 107, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 107, 107, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#FF6B6B' }}>❌ Cons</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Potential merge conflicts</li>
                      <li>Lost cross-component context</li>
                      <li>Some components may still be too large</li>
                      <li>Moderate processing time</li>
                      <li>Multiple API calls required</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approach 3: Smart Token Split */}
          {selectedApproach === 3 && (
            <div ref={approach3Ref}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '30px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                {/* Download button for this approach */}
                <button
                  onClick={() => downloadAsPNG(approach3Ref, 'llm-optimization-smart-split.png')}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    padding: '8px 16px',
                    background: 'rgba(150, 206, 180, 0.2)',
                    color: '#96CEB4',
                    border: '1px solid #96CEB4',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📸 Export
                </button>

                <h2 style={{ margin: '0 0 25px 0', color: '#96CEB4' }}>
                  Approach 3: Smart Token-Based Splitting
                </h2>

                {/* Dynamic Split Visualization */}
                <div style={{ marginBottom: '30px' }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>
                    Dynamic Token-Based Chunks (~500 tokens each)
                  </h3>
                  
                  <div style={{
                    background: 'rgba(150, 206, 180, 0.1)',
                    padding: '20px',
                    borderRadius: '12px',
                    marginBottom: '20px'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#96CEB4' }}>
                      🧠 Intelligent Analysis Phase
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '20px'
                    }}>
                      <div>
                        <h5 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                          Token Distribution
                        </h5>
                        <div style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          padding: '15px',
                          borderRadius: '8px',
                          fontSize: '12px',
                          fontFamily: 'monospace'
                        }}>
                          <div>Total: 15,000 tokens</div>
                          <div>Target chunks: ~500 tokens</div>
                          <div>Actual chunks: 30 chunks</div>
                          <div>Range: 400-600 tokens</div>
                        </div>
                      </div>
                      
                      <div>
                        <h5 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                          Smart Decisions
                        </h5>
                        <div style={{
                          background: 'rgba(0, 0, 0, 0.3)',
                          padding: '15px',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}>
                          <div style={{ marginBottom: '5px' }}>
                            ✅ Process: High-impact functions
                          </div>
                          <div style={{ marginBottom: '5px' }}>
                            ⏭️ Skip: Boilerplate code
                          </div>
                          <div style={{ marginBottom: '5px' }}>
                            🔍 Focus: Performance hotspots
                          </div>
                          <div>
                            📊 Result: 12 chunks selected
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Visual Token Chunks */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(10, 1fr)',
                    gap: '5px',
                    marginBottom: '20px'
                  }}>
                    {Array.from({ length: 30 }, (_, i) => {
                      const isSelected = i % 3 === 0 || i % 5 === 0;
                      const tokens = 400 + Math.floor(Math.random() * 200);
                      
                      return (
                        <div key={i} style={{
                          background: isSelected ? 'rgba(150, 206, 180, 0.3)' : 'rgba(255, 255, 255, 0.05)',
                          border: `1px solid ${isSelected ? '#96CEB4' : 'rgba(255, 255, 255, 0.1)'}`,
                          borderRadius: '4px',
                          padding: '8px',
                          textAlign: 'center',
                          fontSize: '11px',
                          position: 'relative'
                        }}>
                          <div style={{ fontWeight: '600' }}>C{i + 1}</div>
                          <div style={{ opacity: 0.7 }}>{tokens}</div>
                          {isSelected && (
                            <div style={{
                              position: 'absolute',
                              top: '-5px',
                              right: '-5px',
                              background: '#96CEB4',
                              color: 'white',
                              borderRadius: '50%',
                              width: '16px',
                              height: '16px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '10px'
                            }}>
                              ✓
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Processing Strategy */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    padding: '20px',
                    borderRadius: '12px'
                  }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#96CEB4' }}>
                      Processing Strategy
                    </h4>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(4, 1fr)',
                      gap: '10px',
                      fontSize: '12px'
                    }}>
                      <div style={{
                        background: 'rgba(150, 206, 180, 0.1)',
                        padding: '12px',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '5px' }}>🔍</div>
                        <strong>Scan All</strong>
                        <div style={{ opacity: 0.8 }}>30 chunks</div>
                      </div>
                      
                      <div style={{
                        background: 'rgba(150, 206, 180, 0.1)',
                        padding: '12px',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '5px' }}>📊</div>
                        <strong>Analyze</strong>
                        <div style={{ opacity: 0.8 }}>Complexity</div>
                      </div>
                      
                      <div style={{
                        background: 'rgba(150, 206, 180, 0.1)',
                        padding: '12px',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '5px' }}>🎯</div>
                        <strong>Select</strong>
                        <div style={{ opacity: 0.8 }}>12 chunks</div>
                      </div>
                      
                      <div style={{
                        background: 'rgba(150, 206, 180, 0.1)',
                        padding: '12px',
                        borderRadius: '6px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '20px', marginBottom: '5px' }}>🔧</div>
                        <strong>Optimize</strong>
                        <div style={{ opacity: 0.8 }}>& Merge</div>
                      </div>
                    </div>
                  </div>
                </div>

                <ModelRecommendation
                  models={[
                    { name: 'High-Performance Models', reason: 'Best for analysis phase' },
                    { name: 'Large Context Models', reason: 'Good context retention' },
                    { name: 'Efficient Models', reason: 'Cost-effective for many chunks' }
                  ]}
                  color="#96CEB4"
                />

                {/* Pros and Cons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px'
                }}>
                  <div style={{
                    background: 'rgba(76, 175, 80, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#4CAF50' }}>✅ Pros</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Optimal token usage</li>
                      <li>Smart selection of important code</li>
                      <li>High success rate</li>
                      <li>Preserves context effectively</li>
                      <li>Handles any file size</li>
                      <li>Focus on high-impact areas</li>
                    </ul>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255, 107, 107, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 107, 107, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#FF6B6B' }}>❌ Cons</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Complex implementation</li>
                      <li>Longer processing time</li>
                      <li>Requires analysis phase</li>
                      <li>Higher API costs</li>
                      <li>More sophisticated logic needed</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Approach 4: Simple View */}
          {selectedApproach === 4 && (
            <div ref={approach4Ref}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '30px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                {/* Download button for this approach */}
                <button
                  onClick={() => downloadAsPNG(approach4Ref, 'llm-optimization-simple-view.png')}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    padding: '8px 16px',
                    background: 'rgba(156, 39, 176, 0.2)',
                    color: '#9C27B0',
                    border: '1px solid #9C27B0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📸 Export
                </button>

                <h2 style={{ margin: '0 0 25px 0', color: '#9C27B0' }}>
                  Simple File In → File Out View
                </h2>

                {/* Approach 1: Direct LLM */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  padding: '25px',
                  borderRadius: '12px',
                  marginBottom: '25px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', color: '#FF6B6B', fontSize: '20px' }}>
                    1. Direct LLM Approach
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    gap: '30px',
                    alignItems: 'center'
                  }}>
                    {/* File In */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid #FF6B6B',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File In</div>
                      <div style={{ fontSize: '14px', opacity: 0.8 }}>
                        2000 lines<br />
                        15,000 tokens
                      </div>
                    </div>

                    {/* Arrow */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', color: '#FF6B6B' }}>→</div>
                      <div style={{
                        background: 'rgba(255, 107, 107, 0.2)',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        marginTop: '5px'
                      }}>
                        LLM
                      </div>
                    </div>

                    {/* File Out */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px dashed #FF6B6B',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center',
                      opacity: 0.7
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File Out</div>
                      <div style={{ fontSize: '14px', color: '#FF6B6B' }}>
                        ⚠️ Partial optimization<br />
                        ~1000 lines processed
                      </div>
                    </div>
                  </div>
                </div>

                {/* Approach 2: Agent-Based */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  padding: '25px',
                  borderRadius: '12px',
                  marginBottom: '25px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', color: '#FFA726', fontSize: '20px' }}>
                    2. Agent-Based Approach
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr auto 1fr',
                    gap: '30px',
                    alignItems: 'center'
                  }}>
                    {/* File In */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid #FFA726',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File In</div>
                      <div style={{ fontSize: '14px', opacity: 0.8 }}>
                        2000 lines<br />
                        15,000 tokens
                      </div>
                    </div>

                    {/* Agent Analysis */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', color: '#FFA726' }}>→</div>
                      <div style={{
                        background: 'rgba(255, 167, 38, 0.2)',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        marginTop: '5px'
                      }}>
                        🤖 Agent
                      </div>
                    </div>

                    {/* Agent Picks */}
                    <div style={{
                      background: 'rgba(255, 167, 38, 0.1)',
                      border: '1px solid #FFA726',
                      borderRadius: '8px',
                      padding: '15px',
                      minWidth: '150px'
                    }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '10px', textAlign: 'center' }}>
                        Quick Wins
                      </div>
                      <div style={{ fontSize: '12px' }}>
                        <div style={{ marginBottom: '5px' }}>• Hot path: 200 lines</div>
                        <div style={{ marginBottom: '5px' }}>• Bottleneck: 150 lines</div>
                        <div style={{ opacity: 0.6, fontSize: '11px', marginTop: '8px' }}>
                          ⚠️ Ignores 85% of file
                        </div>
                      </div>
                    </div>

                    {/* LLM Arrow */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '36px', color: '#FFA726' }}>→</div>
                      <div style={{
                        background: 'rgba(255, 167, 38, 0.2)',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        marginTop: '5px'
                      }}>
                        LLM
                      </div>
                    </div>

                    {/* File Out */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid #FFA726',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File Out</div>
                      <div style={{ fontSize: '14px', color: '#FFA726' }}>
                        ⚡ Quick wins<br />
                        Limited scope
                      </div>
                    </div>
                  </div>
                </div>

                {/* Approach 3: Class/Function Split */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  padding: '25px',
                  borderRadius: '12px',
                  marginBottom: '25px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', color: '#4ECDC4', fontSize: '20px' }}>
                    3. Class/Function Split Approach
                  </h3>
                  
                  {/* Main Flow */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr auto 1fr',
                    gap: '20px',
                    alignItems: 'center',
                    marginBottom: '30px'
                  }}>
                    {/* File In */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid #4ECDC4',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File In</div>
                      <div style={{ fontSize: '14px', opacity: 0.8 }}>
                        2000 lines<br />
                        15,000 tokens
                      </div>
                    </div>

                    {/* Split Arrow */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', color: '#4ECDC4' }}>→</div>
                      <div style={{ fontSize: '12px', marginTop: '5px' }}>split</div>
                    </div>

                    {/* Multiple Classes with Scores */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      {[
                        { name: 'Class 1', score: 2, desc: 'High complexity' },
                        { name: 'Class 2', score: 4, desc: 'Well structured' },
                        { name: 'Class 3', score: 1, desc: 'Needs refactor' }
                      ].map((item, i) => (
                        <div key={i} style={{
                          background: 'rgba(78, 205, 196, 0.1)',
                          border: '1px solid #4ECDC4',
                          borderRadius: '6px',
                          padding: '10px 15px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <span>{item.name}</span>
                          <div style={{
                            display: 'flex',
                            gap: '2px'
                          }}>
                            {Array.from({ length: 5 }, (_, j) => (
                              <div key={j} style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: j < item.score ? '#4ECDC4' : 'rgba(255, 255, 255, 0.2)'
                              }} />
                            ))}
                          </div>
                        </div>
                      ))}
                      <div style={{ textAlign: 'center', fontSize: '11px', opacity: 0.6 }}>
                        Score: 1-5 (lower = more optimizable)
                      </div>
                    </div>

                    {/* Process Arrow */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', color: '#4ECDC4' }}>→</div>
                      <div style={{ fontSize: '12px', marginTop: '5px' }}>optimize</div>
                    </div>

                    {/* File Out */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid #4ECDC4',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File Out</div>
                      <div style={{ fontSize: '14px', color: '#4ECDC4' }}>
                        ✓ All optimized<br />
                        2000 lines
                      </div>
                    </div>
                  </div>
                </div>

                {/* Approach 4: Smart Token Split */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.2)',
                  padding: '25px',
                  borderRadius: '12px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', color: '#96CEB4', fontSize: '20px' }}>
                    4. Smart Token Split Approach
                  </h3>
                  
                  {/* Main Flow */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr auto 1fr',
                    gap: '20px',
                    alignItems: 'center',
                    marginBottom: '30px'
                  }}>
                    {/* File In */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid #96CEB4',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File In</div>
                      <div style={{ fontSize: '14px', opacity: 0.8 }}>
                        2000 lines<br />
                        15,000 tokens
                      </div>
                    </div>

                    {/* Smart Analysis Arrow */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', color: '#96CEB4' }}>→</div>
                      <div style={{ fontSize: '12px', marginTop: '5px' }}>
                        analyze<br />& filter
                      </div>
                    </div>

                    {/* Auto-Filtered Classes */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div style={{
                        background: 'rgba(150, 206, 180, 0.2)',
                        border: '2px solid #96CEB4',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        textAlign: 'center',
                        fontWeight: '600'
                      }}>
                        🎯 Auto-Selected
                      </div>
                      {[
                        { name: 'Class 1', score: 2, selected: true },
                        { name: 'Class 3', score: 1, selected: true },
                        { name: 'Function 5', score: 2, selected: true }
                      ].map((item, i) => (
                        <div key={i} style={{
                          background: 'rgba(150, 206, 180, 0.1)',
                          border: '1px solid #96CEB4',
                          borderRadius: '6px',
                          padding: '10px 15px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <span>{item.name}</span>
                          <div style={{
                            display: 'flex',
                            gap: '2px'
                          }}>
                            {Array.from({ length: 5 }, (_, j) => (
                              <div key={j} style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: j < item.score ? '#96CEB4' : 'rgba(255, 255, 255, 0.2)'
                              }} />
                            ))}
                          </div>
                        </div>
                      ))}
                      <div style={{ 
                        textAlign: 'center', 
                        fontSize: '11px', 
                        opacity: 0.6,
                        fontStyle: 'italic'
                      }}>
                        Skipped: Class 2 (score: 4)
                      </div>
                    </div>

                    {/* Smart Optimize Arrow */}
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '24px', color: '#96CEB4' }}>→</div>
                      <div style={{ fontSize: '12px', marginTop: '5px' }}>
                        optimize<br />selected
                      </div>
                    </div>

                    {/* File Out */}
                    <div style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '2px solid #96CEB4',
                      borderRadius: '8px',
                      padding: '20px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                      <div style={{ fontWeight: '600', marginBottom: '5px' }}>File Out</div>
                      <div style={{ fontSize: '14px', color: '#96CEB4' }}>
                        ✨ Smart optimized<br />
                        2000 lines
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comparison Table */}
                <div ref={comparisonTableRef} style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '25px',
                  borderRadius: '12px',
                  marginTop: '30px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>
                      📊 Quick Comparison
                    </h3>
                    <button
                      onClick={() => downloadAsPNG(comparisonTableRef, 'llm-optimization-comparison.png')}
                      style={{
                        padding: '6px 12px',
                        background: 'rgba(102, 126, 234, 0.2)',
                        color: '#667eea',
                        border: '1px solid #667eea',
                        borderRadius: '4px',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      📸 Export Table
                    </button>
                  </div>
                  
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px'
                    }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.2)' }}>
                          <th style={{ padding: '10px', textAlign: 'left' }}>Approach</th>
                          <th style={{ padding: '10px', textAlign: 'center' }}>Tokens</th>
                          <th style={{ padding: '10px', textAlign: 'center' }}>Time</th>
                          <th style={{ padding: '10px', textAlign: 'center' }}>Quality</th>
                          <th style={{ padding: '10px', textAlign: 'left' }}>Human Interaction</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <td style={{ padding: '10px', color: '#FF6B6B' }}>Direct LLM</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>~8K</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>⚡ Fast</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>★★☆☆☆</td>
                          <td style={{ padding: '10px', fontSize: '12px' }}>
                            <strong>Minimal:</strong> Single prompt only
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <td style={{ padding: '10px', color: '#FFA726' }}>Agent-Based</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>~10K</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>🚀 Fast</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>★★☆☆☆</td>
                          <td style={{ padding: '10px', fontSize: '12px' }}>
                            <strong>Low:</strong> Prompt + agent config
                          </td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <td style={{ padding: '10px', color: '#4ECDC4' }}>Class/Function</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>~20K</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>⏱️ Moderate</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>★★★★☆</td>
                          <td style={{ padding: '10px', fontSize: '12px' }}>
                            <strong>Medium:</strong> Can filter/select classes
                          </td>
                        </tr>
                        <tr>
                          <td style={{ padding: '10px', color: '#96CEB4' }}>Smart Token</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>~12K</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>⏳ Slower</td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>★★★★★</td>
                          <td style={{ padding: '10px', fontSize: '12px' }}>
                            <strong>High:</strong> Adjust selection criteria
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary */}
                <div style={{
                  marginTop: '30px',
                  padding: '20px',
                  background: 'rgba(156, 39, 176, 0.1)',
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: 0, fontSize: '16px' }}>
                    <strong>Key Difference:</strong> How the file is processed determines the quality of optimization
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Approach 5: Agent-Based */}
          {selectedApproach === 5 && (
            <div ref={approach5Ref}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '30px',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative'
              }}>
                {/* Download button for this approach */}
                <button
                  onClick={() => downloadAsPNG(approach5Ref, 'llm-optimization-agent-based.png')}
                  style={{
                    position: 'absolute',
                    top: '15px',
                    right: '15px',
                    padding: '8px 16px',
                    background: 'rgba(255, 167, 38, 0.2)',
                    color: '#FFA726',
                    border: '1px solid #FFA726',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  📸 Export
                </button>

                <h2 style={{ margin: '0 0 25px 0', color: '#FFA726' }}>
                  Approach 5: Intelligent Agent-Based System
                </h2>

                {/* Visual Process */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '30px',
                  marginBottom: '40px'
                }}>
                  {/* Step 1: Agent Analysis */}
                  <div style={{
                    background: 'rgba(255, 167, 38, 0.1)',
                    padding: '25px',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 167, 38, 0.3)'
                  }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#FFA726', fontSize: '18px' }}>
                      Step 1: Intelligent Agent Analysis
                    </h3>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr auto 1fr',
                      gap: '20px',
                      alignItems: 'center'
                    }}>
                      {/* File In */}
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '2px solid #FFA726',
                        borderRadius: '8px',
                        padding: '20px',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '48px', marginBottom: '10px' }}>📄</div>
                        <div style={{ fontWeight: '600', marginBottom: '5px' }}>File In</div>
                        <div style={{ fontSize: '14px', opacity: 0.8 }}>
                          2000 lines<br />
                          15,000 tokens
                        </div>
                      </div>

                      {/* Agent Arrow */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', color: '#FFA726' }}>→</div>
                        <div style={{
                          background: 'rgba(255, 167, 38, 0.2)',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          marginTop: '5px'
                        }}>
                          🤖 Agent
                        </div>
                      </div>

                      {/* Agent Analysis */}
                      <div style={{
                        background: 'rgba(0, 0, 0, 0.3)',
                        padding: '20px',
                        borderRadius: '8px',
                        minWidth: '200px'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#FFA726' }}>
                          Agent Analysis
                        </h4>
                        <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                          <div style={{ marginBottom: '8px' }}>
                            📊 <strong>Complexity Map:</strong>
                            <div style={{ paddingLeft: '20px', opacity: 0.9 }}>
                              • Hot paths identified<br />
                              • Dependencies mapped<br />
                              • Bottlenecks found
                            </div>
                          </div>
                          <div>
                            🎯 <strong>Smart Decisions:</strong>
                            <div style={{ paddingLeft: '20px', opacity: 0.9 }}>
                              • Skip 60% (already optimal)<br />
                              • Focus on critical 40%<br />
                              • Prioritize by impact
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Decision Arrow */}
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', color: '#FFA726' }}>→</div>
                        <div style={{ fontSize: '12px', marginTop: '5px' }}>
                          decide &<br />prioritize
                        </div>
                      </div>

                      {/* Optimization Plan */}
                      <div style={{
                        background: 'rgba(255, 167, 38, 0.1)',
                        border: '1px solid #FFA726',
                        borderRadius: '8px',
                        padding: '15px',
                        minWidth: '180px'
                      }}>
                        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#FFA726' }}>
                          Optimization Plan
                        </h4>
                        <div style={{ fontSize: '12px' }}>
                          {[
                            { name: 'DataLoader.load()', priority: 'Critical' },
                            { name: 'process_batch()', priority: 'High' },
                            { name: 'validate()', priority: 'Medium' }
                          ].map((item, i) => (
                            <div key={i} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              marginBottom: '6px',
                              padding: '4px 8px',
                              background: 'rgba(255, 167, 38, 0.1)',
                              borderRadius: '4px'
                            }}>
                              <span>{item.name}</span>
                              <span style={{
                                fontSize: '10px',
                                color: item.priority === 'Critical' ? '#FF6B6B' : 
                                      item.priority === 'High' ? '#FFA726' : '#96CEB4',
                                fontWeight: '600'
                              }}>
                                {item.priority}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Targeted Optimization */}
                  <div style={{
                    background: 'rgba(0, 0, 0, 0.2)',
                    padding: '25px',
                    borderRadius: '12px'
                  }}>
                    <h3 style={{ margin: '0 0 20px 0', color: '#FFA726', fontSize: '18px' }}>
                      Step 2: Targeted LLM Optimization
                    </h3>
                    
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '20px'
                    }}>
                      {[
                        { 
                          function: 'DataLoader.load()', 
                          tokens: 800, 
                          improvement: '+85%',
                          before: 'Sequential loading',
                          after: 'Parallel + caching'
                        },
                        { 
                          function: 'process_batch()', 
                          tokens: 600, 
                          improvement: '+70%',
                          before: 'Nested loops',
                          after: 'Vectorized ops'
                        },
                        { 
                          function: 'validate()', 
                          tokens: 400, 
                          improvement: '+40%',
                          before: 'Multiple passes',
                          after: 'Single pass'
                        }
                      ].map((item, i) => (
                        <div key={i} style={{
                          background: 'rgba(255, 167, 38, 0.05)',
                          border: '1px solid rgba(255, 167, 38, 0.3)',
                          borderRadius: '8px',
                          padding: '15px'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '10px'
                          }}>
                            <h4 style={{ margin: 0, fontSize: '14px' }}>{item.function}</h4>
                            <span style={{
                              background: 'rgba(76, 175, 80, 0.2)',
                              color: '#4CAF50',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}>
                              {item.improvement}
                            </span>
                          </div>
                          
                          <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                            <div style={{ opacity: 0.7 }}>Tokens used: {item.tokens}</div>
                          </div>
                          
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto 1fr',
                            gap: '10px',
                            alignItems: 'center',
                            fontSize: '11px'
                          }}>
                            <div style={{
                              background: 'rgba(255, 107, 107, 0.1)',
                              padding: '6px',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              {item.before}
                            </div>
                            <div style={{ color: '#FFA726' }}>→</div>
                            <div style={{
                              background: 'rgba(76, 175, 80, 0.1)',
                              padding: '6px',
                              borderRadius: '4px',
                              textAlign: 'center'
                            }}>
                              {item.after}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Agent Features */}
                <div style={{
                  background: 'rgba(255, 167, 38, 0.1)',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '30px'
                }}>
                  <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#FFA726' }}>
                    🤖 Agent Intelligence Features
                  </h3>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px',
                    fontSize: '13px'
                  }}>
                    <div>
                      <strong>🔍 Code Understanding:</strong>
                      <ul style={{ margin: '5px 0 0 20px', opacity: 0.9 }}>
                        <li>AST analysis</li>
                        <li>Dependency tracking</li>
                        <li>Performance profiling</li>
                      </ul>
                    </div>
                    <div>
                      <strong>🎯 Smart Selection:</strong>
                      <ul style={{ margin: '5px 0 0 20px', opacity: 0.9 }}>
                        <li>Impact scoring</li>
                        <li>Effort estimation</li>
                        <li>Risk assessment</li>
                      </ul>
                    </div>
                    <div>
                      <strong>⚡ Optimization:</strong>
                      <ul style={{ margin: '5px 0 0 20px', opacity: 0.9 }}>
                        <li>Focused token usage</li>
                        <li>Targeted prompts</li>
                        <li>Context preservation</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Comparison Table */}
                <div style={{
                  background: 'rgba(0, 0, 0, 0.3)',
                  padding: '25px',
                  borderRadius: '12px'
                }}>
                  <h3 style={{ margin: '0 0 20px 0', fontSize: '18px' }}>
                    📊 Approach Comparison: Tokens, Time & Quality
                  </h3>
                  
                  <div style={{
                    marginBottom: '20px',
                    padding: '12px',
                    background: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    lineHeight: '1.5'
                  }}>
                    <strong>Note on Quality:</strong> Quality isn't just about coverage - it's a balance of optimization depth, 
                    reliability, and avoiding over-optimization. Smart Token Split may achieve the most comprehensive optimization, 
                    while Agent-Based excels at ROI by targeting high-impact areas but may miss subtle improvements.
                  </div>
                  
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid rgba(255, 255, 255, 0.2)' }}>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Approach</th>
                          <th style={{ padding: '12px', textAlign: 'center' }}>Tokens Spent</th>
                          <th style={{ padding: '12px', textAlign: 'center' }}>Time</th>
                          <th style={{ padding: '12px', textAlign: 'center' }}>Quality</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Best For</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Human Interaction</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <td style={{ padding: '12px' }}>
                            <span style={{ color: '#FF6B6B' }}>1. Direct LLM</span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>~8K</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Single call</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>⚡</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Fastest</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ color: '#FF6B6B', fontSize: '16px' }}>★★☆☆☆</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Partial</div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            Small files only
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            <strong>Minimal:</strong> Single prompt
                          </td>
                        </tr>
                        
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <td style={{ padding: '12px' }}>
                            <span style={{ color: '#FFA726' }}>2. Agent-Based</span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>~10K</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Agent + LLM</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>🚀</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Fast</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ color: '#FFA726', fontSize: '16px' }}>★★☆☆☆</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Targeted</div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            Quick wins only
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            <strong>Low:</strong> Prompt + config
                          </td>
                        </tr>
                        
                        <tr style={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                          <td style={{ padding: '12px' }}>
                            <span style={{ color: '#4ECDC4' }}>3. Class/Function</span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>~20K</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>5-10 calls</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>⏱️</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Moderate</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ color: '#4ECDC4', fontSize: '16px' }}>★★★★☆</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Comprehensive</div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            Well-structured code
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            <strong>Medium:</strong> Filter classes
                          </td>
                        </tr>
                        
                        <tr>
                          <td style={{ padding: '12px' }}>
                            <span style={{ color: '#96CEB4' }}>4. Smart Token</span>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>~12K</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Smart selection</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '20px', marginBottom: '4px' }}>⏳</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Slower</div>
                          </td>
                          <td style={{ padding: '12px', textAlign: 'center' }}>
                            <div style={{ color: '#96CEB4', fontSize: '16px' }}>★★★★★</div>
                            <div style={{ fontSize: '11px', opacity: 0.7 }}>Best Quality</div>
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            Critical optimizations
                          </td>
                          <td style={{ padding: '12px', fontSize: '12px' }}>
                            <strong>High:</strong> Adjust criteria
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: 'rgba(255, 167, 38, 0.1)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    lineHeight: '1.6'
                  }}>
                    <strong>Key Insight:</strong> Agent-based approach uses more tokens than initially shown (~10K vs ~3K) due to the agent's analysis phase. 
                    While it focuses only on high-impact areas, the agent itself requires tokens for code analysis, making the total usage higher than a simple direct approach.
                    The trade-off is targeted optimization vs completeness - agent excels at ROI but may not achieve absolute best quality.
                  </div>
                </div>

                {/* Pros and Cons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginTop: '30px'
                }}>
                  <div style={{
                    background: 'rgba(76, 175, 80, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#4CAF50' }}>✅ Pros</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Excellent ROI on optimizations</li>
                      <li>Fast and efficient</li>
                      <li>Focuses on high-impact areas</li>
                      <li>Avoids unnecessary changes</li>
                      <li>Risk-aware optimization</li>
                      <li>Good for quick improvements</li>
                    </ul>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255, 107, 107, 0.1)',
                    padding: '20px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 107, 107, 0.3)'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#FF6B6B' }}>❌ Cons</h4>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
                      <li>Limited optimization quality</li>
                      <li>Agent memory constraints</li>
                      <li>May miss many opportunities</li>
                      <li>Heuristic-based decisions</li>
                      <li>Can fail on complex patterns</li>
                      <li>Not suitable for deep optimization</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileSplitLLM; 